<template>
  <div class="vue-nav-wrap d-block d-md-flex gap-2 align-items-center">
    <div id="google-one-tap-container" style="position: fixed; top: 200px; right: 20px; z-index: 9999;"></div>

    <!-- Show loading state while user data is being fetched -->
    <template v-if="loading">
      <div class="btn btn-secondary btn-sm d-block d-md-flex my-2 mx-2" style="visibility: hidden;">{{ $t('navbar.loading') }}</div>
    </template>

    <!-- Show appropriate content once user data is loaded -->
    <template v-else>
      <template v-if="user && isProOrProPlus === false">
        <a href="/purchases" class="btn btn-info btn-sm d-block d-md-flex align-items-center justify-content-center my-2 mx-2">{{ $t('navbar.get_pro') }}</a>
      </template>
      <template v-if="user">
        <span class="mx-2 d-none d-lg-block">
          <div class="dropdown">
            <div class="d-flex align-items-center profile-dropdown-wrapper">
              <!-- Profile Link (click to go to profile) -->
              <a href="/user/profile" class="profile-link d-flex align-items-center">
                <template v-if="isProOrProPlus === true">
                  <img class="PRO-tag" src="../../images/PRO-PLUS.png" :alt="$t('navbar.user_pro_status_indicator')" width="35" height="15"/>
                </template>
                <img :alt="$t('navbar.user_profile_picture')"
                     class="rounded-circle img-fluid nav-profile-img"
                     :src="user.avatar_url"
                     :title="$t('navbar.your_profile_picture')"
                     width="60"
                     height="60">
              </a>

              <!-- Dropdown Toggle (click to open menu) -->
              <button
                aria-expanded="false"
                data-bs-toggle="dropdown"
                class="dropdown-toggle-btn btn btn-link p-0"
                type="button">
                <span class="badge badge-pill badge-primary">
                  <span class="material-icons">arrow_drop_down</span>
                </span>
              </button>
            </div>
            <div aria-labelledby="dropdownMenuButton" class="dropdown-menu bg-dark">
              <a class="dropdown-item d-flex align-items-center" href="/user/profile">
                <span class="material-icons mr-2">account_circle</span>
                <div>
                  <p class="m-0"><u><strong>{{ user.display_name || $t('navbar.profile') }}</strong></u></p>
                  <p class="m-0"><small class="text-muted">{{user.email}}</small></p>
                </div>
              </a>
              <div class="dropdown-divider"></div>
              <h6 class="dropdown-header text-muted">{{ $t('navbar.my_activities') }}</h6>
              <a class="dropdown-item d-flex align-items-center" href="/user/hikes">
                <span class="material-icons mr-2">place</span>
                {{ $t('navbar.my_planned_hikes') }}
              </a>
              <a class="dropdown-item d-flex align-items-center" href="/user/recordings">
                <span class="material-icons mr-2">radio_button_checked</span>
                {{ $t('navbar.my_recordings') }}
              </a>
              <a class="dropdown-item d-flex align-items-center" href="/user/hike-lists">
                <span class="material-icons mr-2">bookmark</span>
                {{ $t('navbar.my_hike_lists') }}
              </a>
              <a v-if="user.peak_bagging_web" class="dropdown-item d-flex align-items-center" href="/user/peaks">
                <span class="material-icons mr-2">terrain</span>
                {{ $t('navbar.my_peaks') }}
              </a>
              <a class="dropdown-item d-flex align-items-center" href="/user/print-maps">
                <span class="material-icons mr-2">print</span>
                {{ $t('navbar.my_print_maps') }}
              </a>
              <div class="dropdown-divider"></div>
              <a class="dropdown-item d-flex align-items-center" href="/user/integrations">
                <span class="material-icons mr-2">smart_toy</span>
                {{ $t('navbar.ai_assistant') }}
              </a>
              <div class="dropdown-divider"></div>
              <a v-if="user.admin" class="dropdown-item d-flex align-items-center" href="/admin">
                <span class="material-icons mr-2">admin_panel_settings</span>
                {{ $t('navbar.admin') }}
              </a>
              <div class="dropdown-divider"></div>
              <a class="dropdown-item d-flex align-items-center" href="#" @click.prevent="openSupportChat">
                <span class="material-icons mr-2">support_agent</span>
                {{ $t('navbar.support_chat') }}
              </a>
              <a class="dropdown-item d-flex align-items-center" href="#" @click.prevent="handleLogout">
                <span class="material-icons mr-2">logout</span>
                {{ $t('navbar.logout') }}
              </a>
            </div>
          </div>
        </span>
      </template>

      <template v-else>
        <a href="#" @click.prevent="handleLogin" class="btn btn-secondary d-block d-md-flex align-items-center justify-content-center my-2 mx-2">{{ $t('navbar.login') }}</a>
      </template>
    </template>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';

// --- Firebase Imports ---
// Import functions needed from the SDK
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
// Import the SHARED, initialized auth instance from your auth.js file
import { auth } from '../../utils/firebase/auth.js'; // <-- ADJUST PATH if needed!

// --- Other Imports ---
import bus from '../../utils/bus'; // Import the mitt instance
import graphClient from '../../utils/graphql/graph_client';
import { UpdateUser } from '../../utils/graphql/user-graph'; // Assuming this is correctly typed/defined
import axiosClient from '../../utils/helpers/axios_client'; // Your configured axios instance
import { RevenueCatWrapper } from '../../utils/RevenueCatWrapper'; // Assuming this is compatible or updated
import emitter from "../../utils/bus"; // Assuming this component is Vue 3 compatible
import { i18n } from '../../i18n';
import { OnBoardingGoal } from '../../utils/models/OnBoardingGoal'; // Import OnBoardingGoal enum
import performLogout from '../../utils/auth/logout';

// --- Global Declarations (if needed for Google Sign-In script) ---
declare global {
  interface Window {
    google: any; // Use a more specific type if you have one for Google Identity Services
    googleClientId: string; // Assuming this is set globally elsewhere (e.g., by Rails)
    Intercom: any; // Intercom messenger
    initiateCall: () => void; // Intercom initialization function
    user: any; // User object set by Rails on initial page load
  }
}

// --- Component State ---
const user = ref<any>(null); // Consider defining a User interface/type for better type safety: User | null
const isProOrProPlus = ref(false);
const loading = ref(true); // Start with loading true to prevent flash

// --- Methods ---

const handleLogin = () => {
  // Check if onboarding is available on the page
  if (window.onBoardingVue && window.onBoardingVue.launchFor) {
    // Launch onboarding with CreateAccount goal
    window.onBoardingVue.launchFor(OnBoardingGoal.CreateAccount);
  } else {
    // Fallback to redirect if onboarding is not available
    window.location.href = '/login';
  }
};

const loadGoogleScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.getElementById('google-identity-script')) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = 'google-identity-script';
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const initializeOneTap = () => {
  if (typeof window.google === 'undefined' || !window.google.accounts || !window.google.accounts.id) {
    console.error("Google Identity Services script or id module not loaded yet.");
    return;
  }

  if (!window.googleClientId) {
    console.error("Google Client ID (window.googleClientId) not found.");
    return;
  }

  window.google.accounts.id.disableAutoSelect();

  window.google.accounts.id.initialize({
    client_id: window.googleClientId,
    callback: handleCredentialResponse,
    prompt_parent_id: "google-one-tap-container"
  });

  console.log('Google One Tap initialized with custom container');

  window.google.accounts.id.prompt((notification: any) => {
    console.log('Google One Tap prompt notification:', notification);
  });
};

const generateHikingDisplayName = (): string => {
  const firstWords = [
    'Trail', 'Summit', 'Pine', 'Ridge', 'Peak', 'Forest', 'Cairn', 'Rocky', 'Misty', 'Granite', 'Wander', 'Hidden',
    'Willow', 'Maple', 'Echo', 'Glacier', 'Timber', 'Dusty', 'Boulder', 'Shadow', 'River', 'Alpine', 'Golden', 'Frost'
  ];
  const secondWords = [
    'Walker', 'Explorer', 'Tracker', 'Blazer', 'Seeker', 'Trekker', 'Roamer', 'Climber', 'Adventurer', 'Hiker',
    'Wanderer', 'Nomad', 'Pathfinder', 'Scout', 'Drifter', 'Mountaineer', 'Scrambler', 'Woodsman', 'Outrider', 'Strider'
  ];
  const first = firstWords[Math.floor(Math.random() * firstWords.length)];
  const second = secondWords[Math.floor(Math.random() * secondWords.length)];
  const suffix = Math.floor(Math.random() * 1000);
  return `${first}${second}${suffix}`;
};

// Handles response from Google One Tap or Sign In button
const handleCredentialResponse = (response: any) => { // Add specific type for response if available from Google types
  console.log('Google credential response received:', response.credential ? 'ID token received' : response);
  if (!response.credential) {
    console.error("No credential received from Google.");
    return; // Exit if no token
  }

  loading.value = false;
  const credential = GoogleAuthProvider.credential(response.credential);

  // Sign in to Firebase with the Google credential
  signInWithCredential(auth, credential)
    .then(async (userCredential) => {
      console.log('Firebase sign-in successful:', userCredential.user.uid);
      const token = await userCredential.user.getIdToken();

      // Configure backend clients with Firebase token
      graphClient.setHeader('Authorization', token); // For GraphQL client
      axiosClient.defaults.headers.common['Authorization'] = `Bearer ${token}`; // For Axios client

      // Authenticate with your backend using the Firebase token
      // Assuming this endpoint verifies token and creates/updates Rails session
      await axiosClient.get(`/firebase/auth.json?accessToken=${token}`);
      console.log('Backend authentication request sent.');

      // Check if it's a new user sign-up
      const isNewUser = userCredential.additionalUserInfo?.isNewUser;
      if (isNewUser) {
        console.log('New user detected. Generating display name...');
        const generatedDisplayName = generateHikingDisplayName();
        try {
          // Update user profile (e.g., set generated display name) via GraphQL
          await graphClient.request(UpdateUser, {
            user: {
              title: userCredential.user.displayName || 'New Adventurer', // Use Google name or default
              displayName: generatedDisplayName
            }
          });
          console.log('UpdateUser mutation successful for new user.');
        } catch (e) {
          console.error('UpdateUser mutation failed:', e);
        }
      }
      window.mixpanel.identify(userCredential.user.uid);
      window.mixpanel.people.set({
        '$email': userCredential.user.email,
      });
      window.mixpanel.track(isNewUser ? 'Sign Up Success' : 'Login Success');

      // Signal that user state might have changed
      bus.emit('user:updated'); // This will trigger fetchCurrentUser via the listener

    })
    .catch((error) => {
      console.error('Error signing in with Google credential:', error);
      // Clear potentially stale auth headers if sign-in fails
      delete graphClient.headers['Authorization'];
      delete axiosClient.defaults.headers.common['Authorization'];
    })
    .finally(() => {
      loading.value = false;
    });
};

// Fetches current user data from your backend
const fetchCurrentUser = async () => {
  loading.value = true;
  try {
    // Always fetch from API to get computed fields like avatar_url
    const { data } = await axiosClient.get('/user/current');
    user.value = data; // Update the reactive user state

    if (user.value) {
      // User is logged in to backend, check pro status
      isProOrProPlus.value = await RevenueCatWrapper.isProOrProPlus();
    } else {
      console.log("No active backend session found. Initializing Google One Tap.");
      // User is not logged in to backend, try initializing Google One Tap for sign-in
      user.value = null; // Ensure user state is null
      isProOrProPlus.value = false;
      await loadGoogleScript();
      initializeOneTap();
    }
  } catch (error: any) { // Catch specific error types if possible
    console.warn("Failed to fetch current user (might be logged out):", error.response?.status, error.message);
    user.value = null; // Ensure user is null on error
    isProOrProPlus.value = false;
    // Attempt to load Google One Tap even if fetch fails (assuming user is likely logged out)
    try {
      await loadGoogleScript();
      initializeOneTap();
    } catch (scriptError) {
      console.error('Failed to load Google Identity script:', scriptError);
    }
  } finally {
    loading.value = false;
  }
};

// --- SUPPORT CHAT METHOD ---
const openSupportChat = () => {
  // Ensure the widget script has started loading (no-op if already loaded)
  if (typeof window.initiateCall === 'function') {
    window.initiateCall();
  }

  // Intercom's stub queues 'show' calls until the real widget script loads
  if (typeof window.Intercom === 'function') {
    window.Intercom('show');
  } else {
    console.warn('Intercom widget not available yet.');
  }
};

// --- COORDINATED LOGOUT METHOD ---
const handleLogout = async () => {
  console.log('Logout initiated...');
  loading.value = true;

  try {
    await performLogout();

    user.value = null;
    isProOrProPlus.value = false;
    emitter.emit('user:updated');

    window.location.href = '/login';
  } catch (error) {
    console.error('Error during logout:', error);
    alert(i18n.global.t('navbar.logout_failed'));
  } finally {
    loading.value = false;
  }
};

// --- Lifecycle Hooks ---

// Handler for the 'user:updated' event emitted by the event bus
const onUserUpdated = () => {
  fetchCurrentUser(); // Re-fetch user data when signaled
};

// Runs when the component is mounted to the DOM
onMounted(async () => {
  await fetchCurrentUser(); // Fetch user state when component loads
  bus.on('user:updated', onUserUpdated); // Listen for user updates from other parts of the app
});

// Runs just before the component is unmounted
onBeforeUnmount(() => {
  bus.off('user:updated', onUserUpdated); // Stop listening to prevent memory leaks
});

</script>

<style scoped>
.PRO-tag {
  position: absolute;
  top: 2px;
  left: 27px; /* Adjust as needed */
  box-shadow: 5px 4px 5px 0px rgb(0 0 0 / 30%);
  -webkit-box-shadow: 5px 4px 5px 0px rgb(0 0 0 / 30%);
  -moz-box-shadow: 5px 4px 5px 0px rgba(0,0,0,0.3);
  z-index: 1; /* Ensure it's above the profile image */
}

/* Profile dropdown wrapper - contains both link and toggle button */
.profile-dropdown-wrapper {
  gap: 0;
  position: relative;
}

/* Profile link styling */
.profile-link {
  position: relative;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: opacity 0.2s;
}

.profile-link:hover {
  opacity: 0.85;
}

/* Dropdown toggle button */
.dropdown-toggle-btn {
  border: none;
  background: none;
  text-decoration: none;
  margin-left: -4px; /* Overlap slightly for cohesive appearance */
}

.dropdown-toggle-btn:hover,
.dropdown-toggle-btn:focus {
  text-decoration: none;
  background: none;
  box-shadow: none;
}

/* Adjust margin/padding if needed for icon alignment */
.dropdown-item .material-icons {
  margin-right: 8px; /* Same as mr-2 */
}

.nav-profile-img {
  vertical-align: middle; /* Align image nicely */
}

.badge {
  vertical-align: middle; /* Align dropdown arrow nicely */
}


@media (max-width: 720px) { /* Consider Bootstrap breakpoint (md: 768px) */
  .vue-nav-wrap {
    width: 100%;
  }
}
</style>

<style>
/* Force override Google One Tap container */
body > iframe[style*="position: fixed"] {
  top: 200px !important;
  right: 20px !important;
  left: auto !important;
  z-index: 9999 !important;
}

/* Optional: narrow screens */
@media (max-width: 768px) {
  body > iframe[style*="position: fixed"] {
    top: 100px !important;
    right: 10px !important;
  }
}
</style>
